// 2D Vertical Breathing Ring for the Buddha Game.
// Creates a vertical plane with a two-layer ring:
// 1. Base Ring: Static soft white circle outline (always visible)
// 2. Progress Arc: Animated arc that grows/shrinks with breathing phase

import {
  PlaneGeometry,
  Mesh,
  MeshBasicMaterial,
  CanvasTexture,
  AdditiveBlending,
  DoubleSide,
} from 'three';

// ============================================================================
// CONFIGURATION
// ============================================================================

const RING_CONFIG = {
  // Ring dimensions on the plane
  // Increased so the breath ring scales up and frames the Buddha more prominently
  planeSize: 2.0, // Size of the plane (width and height)

  // Base ring (background - always visible)
  // Reduce the ring radius slightly to leave padding around the texture
  // (prevents stroke/glow from being clipped at the canvas edge).
  baseRingRadius: 0.45, // Center of the ring stroke (as fraction of plane)
  baseRingThickness: 0.02, // Thickness of the base ring stroke
  baseRingColor: { r: 255, g: 255, b: 255 }, // Soft white
  baseRingOpacity: 0.25, // Subtle visibility

  // Progress arc (foreground - animated)
  // Match progress arc radius to the base ring, slightly reduced for padding.
  progressArcRadius: 0.45, // Same as base ring (larger) to match plane size
  progressArcThickness: 0.035, // Slightly thicker than base to be visible at bigger scale
  progressArcColor: { r: 78, g: 198, b: 232 }, // Soft teal (#4EC6E8)
  progressArcGlowColor: { r: 120, g: 220, b: 255 }, // Lighter teal for glow
  progressArcOpacity: 0.85,

  // Glow effect
  // Slightly smaller glow so the outer glow doesn't extend beyond canvas
  // when we draw at higher resolution.
  glowSize: 0.035, // Size of outer glow
  glowOpacity: 0.55, // Glow intensity

  // Position relative to Buddha center (inside buddhaGroup)
  // Align defaults with the Buddha model debug defaults so the ring
  // visually centers around the model by default (matches loadCharacter x/y).
  xOffset: 0.29,
  yOffset: 0.11, // Vertical offset (center around Buddha's chest/head area)
  // Place the ring at the same Z as the Buddha model so it centers
  // visually when both are in the same parent group.
  zOffset: -2.5, // Same Z as default Buddha model

  // Arc direction: true = clockwise, false = counter-clockwise
  clockwise: true,

  // Start angle (0 = top, Math.PI/2 = right, Math.PI = bottom, -Math.PI/2 = left)
  startAngle: -Math.PI / 2, // Start from top
};

// ============================================================================
// BREATH RING STATE
// ============================================================================

interface BreathRingState {
  mesh: Mesh | null;
  material: MeshBasicMaterial | null;
  texture: CanvasTexture | null;
  canvas: HTMLCanvasElement | null;
  ctx: CanvasRenderingContext2D | null;
  initialized: boolean;
  // Current arc progress (0-1)
  currentArcProgress: number;
  targetArcProgress: number;
  // Last drawn progress (to avoid unnecessary redraws)
  lastDrawnProgress: number;
  lastDrawnValidWindowActive: boolean;
  lastDrawnValidWindowEnd: number;
  // Debug values
  debugEnabled: boolean;
  // Allow runtime position overrides (mesh.position is authoritative if present)
  overridePosition: { x?: number; y?: number; z?: number } | null;
}

const ringState: BreathRingState = {
  mesh: null,
  material: null,
  texture: null,
  canvas: null,
  ctx: null,
  initialized: false,
  currentArcProgress: 0,
  targetArcProgress: 0,
  lastDrawnProgress: -1, // Force initial draw
  lastDrawnValidWindowActive: false,
  lastDrawnValidWindowEnd: 0,
  debugEnabled: false,
  overridePosition: null,
};

// ============================================================================
// RING TEXTURE CREATION
// ============================================================================

/**
 * Draw the base ring (static background circle).
 */
function drawBaseRing(ctx: CanvasRenderingContext2D, size: number): void {
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = RING_CONFIG.baseRingRadius * size;
  const thickness = RING_CONFIG.baseRingThickness * size;

  const { r, g, b } = RING_CONFIG.baseRingColor;
  const alpha = RING_CONFIG.baseRingOpacity;

  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
  ctx.lineWidth = thickness;
  ctx.lineCap = 'round';
  ctx.stroke();
}

/**
 * Draw a subtle highlight on the start segment of the ring while the
 * phase input window is active.
 */
function drawValidWindowHighlight(
  ctx: CanvasRenderingContext2D,
  size: number,
  validWindowEndRatio: number
): void {
  if (validWindowEndRatio <= 0) return;

  const centerX = size / 2;
  const centerY = size / 2;
  const radius = RING_CONFIG.baseRingRadius * size;
  const thickness = RING_CONFIG.baseRingThickness * size;

  const startAngle = RING_CONFIG.startAngle;
  const arcLength = Math.PI * 2 * Math.min(1, Math.max(0, validWindowEndRatio));
  const endAngle = RING_CONFIG.clockwise
    ? startAngle + arcLength
    : startAngle - arcLength;

  const alpha = Math.min(1, RING_CONFIG.baseRingOpacity + 0.12);

  ctx.beginPath();
  if (RING_CONFIG.clockwise) {
    ctx.arc(centerX, centerY, radius, startAngle, endAngle, false);
  } else {
    ctx.arc(centerX, centerY, radius, startAngle, endAngle, true);
  }
  ctx.strokeStyle = `rgba(255, 255, 255, ${alpha})`;
  ctx.lineWidth = thickness;
  ctx.lineCap = 'round';
  ctx.stroke();
}

/**
 * Draw the progress arc (animated foreground).
 * @param progress - Progress from 0 to 1
 * @param isFollowingPattern - If false, use error/red color for the arc
 */
function drawProgressArc(
  ctx: CanvasRenderingContext2D,
  size: number,
  progress: number,
  isFollowingPattern: boolean = true
): void {
  if (progress <= 0) return;

  const centerX = size / 2;
  const centerY = size / 2;
  const radius = RING_CONFIG.progressArcRadius * size;
  const thickness = RING_CONFIG.progressArcThickness * size;
  const glowSize = RING_CONFIG.glowSize * size;

  // Calculate arc angles
  const startAngle = RING_CONFIG.startAngle;
  const arcLength = Math.PI * 2 * Math.min(1, Math.max(0, progress));
  const endAngle = RING_CONFIG.clockwise
    ? startAngle + arcLength
    : startAngle - arcLength;

  // Use error colors if not following pattern
  const arcColor = isFollowingPattern
    ? RING_CONFIG.progressArcColor
    : { r: 255, g: 80, b: 80 }; // Red for error
  const glowColor = isFollowingPattern
    ? RING_CONFIG.progressArcGlowColor
    : { r: 255, g: 120, b: 120 }; // Light red for error glow

  const { r, g, b } = arcColor;
  const { r: gr, g: gg, b: gb } = glowColor;

  // Draw outer glow first (larger, more transparent)
  ctx.beginPath();
  if (RING_CONFIG.clockwise) {
    ctx.arc(centerX, centerY, radius, startAngle, endAngle, false);
  } else {
    ctx.arc(centerX, centerY, radius, startAngle, endAngle, true);
  }
  ctx.strokeStyle = `rgba(${gr}, ${gg}, ${gb}, ${RING_CONFIG.glowOpacity * 0.3})`;
  ctx.lineWidth = thickness + glowSize * 2;
  ctx.lineCap = 'round';
  ctx.stroke();

  // Draw medium glow
  ctx.beginPath();
  if (RING_CONFIG.clockwise) {
    ctx.arc(centerX, centerY, radius, startAngle, endAngle, false);
  } else {
    ctx.arc(centerX, centerY, radius, startAngle, endAngle, true);
  }
  ctx.strokeStyle = `rgba(${gr}, ${gg}, ${gb}, ${RING_CONFIG.glowOpacity * 0.5})`;
  ctx.lineWidth = thickness + glowSize;
  ctx.lineCap = 'round';
  ctx.stroke();

  // Draw main progress arc
  ctx.beginPath();
  if (RING_CONFIG.clockwise) {
    ctx.arc(centerX, centerY, radius, startAngle, endAngle, false);
  } else {
    ctx.arc(centerX, centerY, radius, startAngle, endAngle, true);
  }
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${RING_CONFIG.progressArcOpacity})`;
  ctx.lineWidth = thickness;
  ctx.lineCap = 'round';
  ctx.stroke();

  // Draw bright inner highlight for extra glow effect
  ctx.beginPath();
  if (RING_CONFIG.clockwise) {
    ctx.arc(centerX, centerY, radius, startAngle, endAngle, false);
  } else {
    ctx.arc(centerX, centerY, radius, startAngle, endAngle, true);
  }
  ctx.strokeStyle = `rgba(255, 255, 255, 0.3)`;
  ctx.lineWidth = thickness * 0.4;
  ctx.lineCap = 'round';
  ctx.stroke();
}

/**
 * Draw the complete ring (base + progress arc).
 * @param isFollowingPattern - If false, use error color for the arc
 */
function drawRing(
  ctx: CanvasRenderingContext2D,
  size: number,
  progress: number,
  isFollowingPattern: boolean = true,
  validWindowEndRatio: number = 0,
  validWindowActive: boolean = false
): void {
  // Clear the canvas
  ctx.clearRect(0, 0, size, size);

  // Draw base ring (always visible)
  drawBaseRing(ctx, size);

  // Highlight the start segment only while the valid window is active
  if (validWindowActive) {
    drawValidWindowHighlight(ctx, size, validWindowEndRatio);
  }

  // Draw progress arc on top (with error color if not following pattern)
  drawProgressArc(ctx, size, progress, isFollowingPattern);

  // If debug overlay is enabled, draw crosshair and debug text
  if (ringState.debugEnabled) {
    // center crosshair
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(size / 2 - 10, size / 2);
    ctx.lineTo(size / 2 + 10, size / 2);
    ctx.moveTo(size / 2, size / 2 - 10);
    ctx.lineTo(size / 2, size / 2 + 10);
    ctx.stroke();

    // draw debug text for position (we'll show placeholders - scene will show exact values elsewhere)
    ctx.font = '14px system-ui, Arial';
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.textAlign = 'center';
    ctx.fillText('ring debug', size / 2, size - 18);
    ctx.restore();
  }
}

/**
 * Create a canvas texture with the ring.
 */
function createRingTexture(): {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  texture: CanvasTexture;
} {
  // Use a larger canvas to provide room for glow and stroke without clipping.
  // 768 gives a generous margin while remaining reasonably performant.
  const size = 768;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  // Enable anti-aliasing
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Draw initial state (base ring only, no progress)
  drawRing(ctx, size, 0, true, 0, false);

  const texture = new CanvasTexture(canvas);
  texture.needsUpdate = true;

  return { canvas, ctx, texture };
}

// ============================================================================
// RING CREATION
// ============================================================================

/**
 * Create the 2D breathing ring mesh.
 * Returns a vertical plane with a transparent ring texture.
 */
export function createBreathRing(): Mesh {
  // Create the ring texture
  const { canvas, ctx, texture } = createRingTexture();

  // Store references for later updates
  ringState.canvas = canvas;
  ringState.ctx = ctx;
  ringState.texture = texture;

  // Create plane geometry (vertical, facing camera)
  const geometry = new PlaneGeometry(
    RING_CONFIG.planeSize,
    RING_CONFIG.planeSize
  );

  // Create material with transparency and additive blending
  const material = new MeshBasicMaterial({
    map: texture,
    transparent: true,
    opacity: 1.0, // Full opacity - the canvas handles transparency
    side: DoubleSide,
    depthWrite: false,
    blending: AdditiveBlending,
  });

  const mesh = new Mesh(geometry, material);

  // Position the ring behind the Buddha using configured offsets
  mesh.position.set(
    RING_CONFIG.xOffset,
    RING_CONFIG.yOffset,
    RING_CONFIG.zOffset
  );

  // The plane is already vertical by default (facing +Z)
  // No rotation needed - it naturally faces the camera

  // Render before the Buddha
  mesh.renderOrder = -400;

  // Store references
  ringState.mesh = mesh;
  ringState.material = material;
  ringState.initialized = true;

  // eslint-disable-next-line no-console
  console.debug('loadBreathRing: 2D progress ring created');

  return mesh;
}

// ============================================================================
// RING ANIMATION
// ============================================================================

export type BreathingPhase =
  | 'inhale'
  | 'holdAfterInhale'
  | 'exhale'
  | 'holdAfterExhale';

/**
 * Update the breath ring animation.
 * Should be called every frame.
 * @param elapsed - Total elapsed time in seconds
 * @param dt - Delta time since last frame
 * @param phase - Current breathing phase
 * @param progress - Progress through current phase (0-1)
 * @param isActive - Whether breathing is active
 * @param isFollowingPattern - Whether user is following the breathing pattern correctly
 */
export function updateBreathRing(
  _elapsed: number,
  dt: number,
  phase: BreathingPhase,
  progress: number,
  isActive: boolean,
  isFollowingPattern: boolean = true,
  validWindowEndRatio: number = 0,
  validWindowActive: boolean = false
): void {
  if (!ringState.ctx || !ringState.texture || !ringState.canvas) return;

  // If user breaks timing, flash/pulse the ring to indicate error
  // and potentially reset or pause the arc progress
  const effectiveProgress = isFollowingPattern ? progress : progress;

  // Calculate target arc progress based on phase
  if (isActive) {
    switch (phase) {
      case 'inhale':
        // Inhale: arc grows from 0 to 100%
        ringState.targetArcProgress = effectiveProgress;
        break;

      case 'holdAfterInhale':
        // Hold after inhale: arc stays at 100%
        ringState.targetArcProgress = 1.0;
        break;

      case 'exhale':
        // Exhale: arc shrinks from 100% to 0%
        ringState.targetArcProgress = 1.0 - effectiveProgress;
        break;

      case 'holdAfterExhale':
        // Hold after exhale: arc stays at 0%
        ringState.targetArcProgress = 0;
        break;
    }
  } else {
    // Not active: show no progress
    ringState.targetArcProgress = 0;
  }

  // Smooth interpolation to target (very responsive for accurate timing)
  const lerpSpeed = 15.0;
  ringState.currentArcProgress +=
    (ringState.targetArcProgress - ringState.currentArcProgress) *
    lerpSpeed *
    dt;

  // Clamp to valid range
  ringState.currentArcProgress = Math.max(
    0,
    Math.min(1, ringState.currentArcProgress)
  );

  // Only redraw if progress has changed significantly
  const progressChanged =
    Math.abs(ringState.currentArcProgress - ringState.lastDrawnProgress) >
    0.005;

  const windowChanged =
    ringState.lastDrawnValidWindowActive !== validWindowActive ||
    Math.abs(ringState.lastDrawnValidWindowEnd - validWindowEndRatio) > 0.001;

  if (progressChanged || windowChanged) {
    drawRing(
      ringState.ctx,
      ringState.canvas.width,
      ringState.currentArcProgress,
      isFollowingPattern,
      validWindowEndRatio,
      validWindowActive
    );
    ringState.texture.needsUpdate = true;
    ringState.lastDrawnProgress = ringState.currentArcProgress;
    ringState.lastDrawnValidWindowActive = validWindowActive;
    ringState.lastDrawnValidWindowEnd = validWindowEndRatio;
  }
}

// ============================================================================
// DEBUG / POSITIONING API
// ============================================================================

/**
 * Enable or disable debug overlay on the ring texture.
 * When enabled the canvas will show a crosshair and label.
 */
export function enableRingDebug(enabled: boolean) {
  ringState.debugEnabled = !!enabled;
  if (ringState.ctx && ringState.canvas && ringState.texture) {
    drawRing(
      ringState.ctx,
      ringState.canvas.width,
      ringState.currentArcProgress,
      true,
      ringState.lastDrawnValidWindowEnd,
      ringState.lastDrawnValidWindowActive
    );
    ringState.texture.needsUpdate = true;
  }
}

/**
 * Set ring position (world units) — also stores override so debug can report it.
 */
export function setRingPosition(x: number, y: number, z: number) {
  ringState.overridePosition = { x, y, z };
  if (ringState.mesh) {
    ringState.mesh.position.set(x, y, z);
  }
}

/**
 * Move the ring by a delta and keep override in sync.
 */
export function moveRingBy(dx: number, dy: number, dz: number) {
  if (ringState.mesh) {
    ringState.mesh.position.x += dx;
    ringState.mesh.position.y += dy;
    ringState.mesh.position.z += dz;
    ringState.overridePosition = {
      x: ringState.mesh.position.x,
      y: ringState.mesh.position.y,
      z: ringState.mesh.position.z,
    };
  } else if (ringState.overridePosition) {
    ringState.overridePosition.x = (ringState.overridePosition.x ?? 0) + dx;
    ringState.overridePosition.y = (ringState.overridePosition.y ?? 0) + dy;
    ringState.overridePosition.z = (ringState.overridePosition.z ?? 0) + dz;
  }
}

/**
 * Get current ring position (mesh position if present, otherwise overrides or config)
 */
export function getRingPosition() {
  if (ringState.mesh) {
    return {
      x: ringState.mesh.position.x,
      y: ringState.mesh.position.y,
      z: ringState.mesh.position.z,
    };
  }
  if (ringState.overridePosition) return { ...ringState.overridePosition };
  return {
    x: RING_CONFIG.xOffset,
    y: RING_CONFIG.yOffset,
    z: RING_CONFIG.zOffset,
  };
}

// ============================================================================
// RING MANAGEMENT
// ============================================================================

/**
 * Get the ring mesh.
 */
export function getBreathRingMesh(): Mesh | null {
  return ringState.mesh;
}

/**
 * Check if the ring is initialized.
 */
export function isBreathRingInitialized(): boolean {
  return ringState.initialized;
}

/**
 * Dispose of the ring resources.
 */
export function disposeBreathRing(): void {
  if (ringState.mesh) {
    if (ringState.mesh.geometry) {
      ringState.mesh.geometry.dispose();
    }
    if (ringState.material) {
      if (ringState.texture) {
        ringState.texture.dispose();
      }
      ringState.material.dispose();
    }
    ringState.mesh = null;
    ringState.material = null;
    ringState.texture = null;
    ringState.canvas = null;
    ringState.ctx = null;
  }
  ringState.initialized = false;
  // eslint-disable-next-line no-console
  console.debug('loadBreathRing: ring disposed');
}

/**
 * Reset ring animation state.
 */
export function resetBreathRing(): void {
  ringState.currentArcProgress = 0;
  ringState.targetArcProgress = 0;
  ringState.lastDrawnProgress = -1; // Force redraw
  ringState.lastDrawnValidWindowActive = false;
  ringState.lastDrawnValidWindowEnd = 0;

  if (ringState.ctx && ringState.canvas && ringState.texture) {
    drawRing(ringState.ctx, ringState.canvas.width, 0, true, 0, false);
    ringState.texture.needsUpdate = true;
  }
}

/**
 * Get ring state for debugging.
 */
export function getBreathRingState(): {
  initialized: boolean;
  opacity: number;
  scale: number;
} {
  return {
    initialized: ringState.initialized,
    opacity: 1.0,
    scale: 1.0,
  };
}

export default {
  createBreathRing,
  updateBreathRing,
  getBreathRingMesh,
  isBreathRingInitialized,
  disposeBreathRing,
  resetBreathRing,
  getBreathRingState,
  // Debug / positioning helpers
  enableRingDebug,
  setRingPosition,
  moveRingBy,
  getRingPosition,
};
